import theme from "components/MuiThemeProvider/theme";
import KeyboardDoubleArrowUp from "@mui/icons-material/KeyboardDoubleArrowUp";
import KeyboardDoubleArrowDown from "@mui/icons-material/KeyboardDoubleArrowDown";
import { DeltaType, DeltaTypes } from "types/delta-types";
import type { WidgetCommonData, WidgetDataset } from "features/Dashboards/providers/WidgetCommonDataProvider";
import { MetadataItem, MetadataType, Variants } from "types";
import groupBy from "lodash/groupBy";
import moment from "moment";
import type { MeasureData } from "services/MeasureService/MeasureService.types";
import { SortType } from "types/output-exercise";
import type { GroupWidgetDataBy } from "./WidgetHelpers.types";
import TimeHelper from "helpers/TimeHelper";
import { Dashboard, DashboardTimePeriod } from "types/Dashboard";
import { Period } from "hooks/useFetchAggregatedMeasures";
import WidgetTargetHelpers from "helpers/WidgetTargetHelpers";
import mean from "lodash/mean";
import { SessionData } from "features/Dashboards/DashboardsHome/DashboardWidgetFactory/components/WidgetSessionViewHandler";

class WidgetHelpers {
  static useOptions = [
    {
      label: "dashboard.widgets_use_average_option",
      value: "Average",
    },
    {
      label: "dashboard.widgets_use_best_option",
      value: "Best",
    },
  ];
  static filterFromOptions = [
    {
      label: "dashboard.widgets_period_Latest",
      value: "Latest",
    },
    {
      label: "dashboard.widgets_period_P1D",
      value: "P1D",
    },
    {
      label: "dashboard.widgets_period_P7D",
      value: "P7D",
    },
    {
      label: "dashboard.widgets_period_P30D",
      value: "P30D",
    },
    {
      label: "dashboard.widgets_period_P90D",
      value: "P90D",
    },
    {
      label: "dashboard.widgets_period_P365D",
      value: "P365D",
    },
  ];

  /**
   * @description
   * Construct a list of categories from the data
   * @param data - The data to construct the categories from
   * @param category - The key to use as the category
   * @returns A list of categories
   */
  static constructCategories<T>(data: T[], category?: keyof T): string[] {
    if (!category) {
      return [];
    }

    const categories = new Set<string>();

    data.forEach((datum) => {
      categories.add(String(datum[category]));
    });

    return Array.from(categories);
  }

  /**
   * @description
   * Construct a map of categories and their corresponding colors
   * @param categories - The list of categories
   * @param colors - The list of colors
   * @returns A map of categories and their corresponding colors
   * @example
   * ```ts
   * const categories = ["Squat", "Bench Press", "Dead-lift"];
   * const colors = ["#FF0", "#00F", "#000"];
   * const categoryColors = WidgetHelpers.constructCategoryColors(categories, colors);
   * ```
   */
  static constructCategoryColors(categories: string[], colors: string[]): Map<string, string> {
    const categoryColors = new Map<string, string>();

    categories.forEach((category, idx) => {
      categoryColors.set(category, colors[idx % colors.length]);
    });

    return categoryColors;
  }

  /**
   * @description Format a percentage absolute value. E.g 20%.
   * @returns a formatted percentage value
   * @example
   * ```ts
   * const delta = 02;
   * const formattedDelta = WidgetHelpers.formatPercentageValue(delta);
   * ```
   */
  static formatPercentageValue = (delta: number | null) => {
    if (delta === null) {
      return "0%";
    }

    return `${Math.abs(delta)}%`;
  };

  /**
   * @description Format a percentage value with a +/- sign for positive or negative values and -- for null values.
   * @returns a formatted percentage value
   * @example
   * ```ts
   * const delta = 0.2;
   * const formattedDelta = WidgetHelpers.formatPercentageValueWithSign(delta);
   * ```
   */
  static formatPercentageValueWithSign = (delta: number | null) => {
    if (delta === null) {
      return "0%";
    }

    return delta > 0 ? `+${delta}%` : `${delta}%`;
  };

  /**
   * @name calculateAsymmetry.
   * @description Calculate the asymmetry between two values. The difference between the best and worst value.
   * @formula ((best - worse) / worse) * 100
   * @returns The asymmetry percentage
   */
  static calculateAsymmetry(value1: number, value2: number) {
    if (value1 === 0 || value2 === 0) {
      return null;
    }

    const bestValue = Math.max(value1, value2);
    const worstValue = Math.min(value1, value2);

    return Math.abs(Math.round(((bestValue - worstValue) / worstValue) * 100));
  }

  /**
   * @name buildAsymmetryMessage.
   * @description Build the asymmetry message. E.g Right side is 10% better than left side
   * @returns The asymmetry values
   */
  static buildAsymmetryMessage = (right: number, left: number) => {
    const asymmetry = WidgetHelpers.calculateAsymmetry(right, left);

    if (asymmetry === null) {
      return null;
    }

    const messageKey =
      right > left ? "dashboard.widget_kpi_asymmetry_right_better" : "dashboard.widget_kpi_asymmetry_left_better";
    const asymmetryValue = WidgetHelpers.formatPercentageValue(asymmetry);

    return { messageKey, asymmetry: asymmetryValue };
  };

  /**
   *
   * @description Get the styles for the delta type. Increase, Decrease, Neutral.
   * It follows the same pattern as the colors in the theme and design system.
   * @returns The styles for the delta type (bgColor, textColor, ringColor, iconColor)
   * @example
   * ```ts
   * const deltaType = DeltaTypes.Increase;
   * const deltaTypeStyles = WidgetHelpers.getDeltaTypeStyles(deltaType);
   * ```
   */
  static getDeltaTypeStyles(deltaType: DeltaType) {
    switch (deltaType) {
      case DeltaTypes.Increase:
        return {
          bgColor: theme.palette.secondary1[100],
          textColor: theme.palette.common.white,
          ringColor: theme.palette.secondary1[100],
          iconColor: theme.palette.common.white,
        };
      case DeltaTypes.Decrease:
        return {
          bgColor: theme.palette.secondary2[100],
          textColor: theme.palette.common.white,
          ringColor: theme.palette.secondary2[100],
          iconColor: theme.palette.common.white,
        };
      case DeltaTypes.HitTarget:
        return {
          bgColor: theme.palette.secondary1[100],
          textColor: theme.palette.common.white,
          ringColor: theme.palette.secondary1[100],
          iconColor: theme.palette.common.white,
        };
      case DeltaTypes.BelowTarget:
      default:
        return {
          bgColor: theme.palette.grey[100],
          textColor: theme.palette.common.white,
          ringColor: theme.palette.grey[100],
          iconColor: theme.palette.grey[20],
        };
    }
  }

  /**
   * @description Get the icons for the delta type. Increase and Decrease.
   * @important Neutral is not included because it does not have an icon.
   */
  static getDeltaTypeIcons(deltaType: DeltaType) {
    switch (deltaType) {
      case DeltaTypes.Increase:
        return KeyboardDoubleArrowUp;
      case DeltaTypes.Decrease:
        return KeyboardDoubleArrowDown;
      default:
        return null;
    }
  }

  /**
   * @description Map the inputs to the delta type. Increase, Decrease, Neutral. It is important to know if an increase is positive or negative, to determine the delta type.
   * @param deltaType - The delta type
   * @param isIncreasePositive - Whether an increase is positive
   * @returns The mapped delta type based on the inputs
   */
  static mapInputsToDeltaType(deltaType: DeltaType, isIncreasePositive: boolean): DeltaType {
    if (isIncreasePositive || deltaType === DeltaTypes.Neutral) {
      return deltaType;
    }

    switch (deltaType) {
      case DeltaTypes.Increase:
        return DeltaTypes.Decrease;
      case DeltaTypes.Decrease:
        return DeltaTypes.Increase;
      default:
        return DeltaTypes.Neutral;
    }
  }

  static groupWidgetDataByDate(data: Array<WidgetDataset>) {
    return groupBy(data, (item) => moment(item.completedDate).format("YYYY-MM-DD"));
  }

  static groupWidgetDataByWeek(data: Array<WidgetDataset>) {
    return groupBy(data, (item) => `${moment(item.completedDate).year()}-${moment(item.completedDate).week()}`);
  }

  static groupWidgetDataByMonth(data: Array<WidgetDataset>) {
    return groupBy(data, (item) => `${moment(item.completedDate).year()}-${moment(item.completedDate).month()}`);
  }

  static getGroupByFunction(groupBy?: GroupWidgetDataBy) {
    if (groupBy === "day") return WidgetHelpers.groupWidgetDataByDate;
    if (groupBy === "week") return WidgetHelpers.groupWidgetDataByWeek;
    if (groupBy === "month") return WidgetHelpers.groupWidgetDataByMonth;
    return WidgetHelpers.groupWidgetDataByDate;
  }

  static formatGroupedByDate(date: string, groupBy: GroupWidgetDataBy) {
    if (groupBy === "week") return moment(date).startOf("week").toISOString();
    if (groupBy === "month") return moment(date).startOf("month").toISOString();
    return date;
  }

  static filterMeasurementsBeforeDate(measureData: MeasureData, latestAllowedDate: moment.Moment): MeasureData {
    if (measureData.type === MetadataType.ANTHROPOMETRIC) {
      return {
        ...measureData,
        measurements: measureData.measurements.filter((m) => moment(m.measurementDate).isBefore(latestAllowedDate)),
      };
    } else if (measureData.type === MetadataType.WELLNESS) {
      return {
        ...measureData,
        measurements: measureData.measurements.filter((m) => moment(m.measurementDate).isBefore(latestAllowedDate)),
      };
    }
    return {
      ...measureData,
      measurements: measureData.measurements.filter((m) => moment(m.completedDate).isBefore(latestAllowedDate)),
    };
  }

  static checkLatestSessionOutdated(date: string) {
    return moment(date).isBefore(moment().endOf("day").subtract(7, "days"));
  }

  static buildAggregationDateRangePeriod(period: DashboardTimePeriod): Period {
    if (period.type === "SecondLatest") {
      const range = TimeHelper.getRelativePeriodDates("P365D")!;
      return { startDate: range.start, endDate: range.end, type: period.type };
    }

    if (!period.relativePeriod || period.relativePeriod === "Latest") {
      const range = TimeHelper.getRelativePeriodDates("P365D")!;
      return { startDate: range.start, endDate: range.end, type: period.type };
    }

    const range = TimeHelper.getRelativePeriodDates(period.relativePeriod)!;
    return { startDate: range.start, endDate: range.end, type: period.type };
  }

  static createUniqueId(args: {
    athleteId: string;
    metadata: Pick<MetadataItem, "id" | "field">;
    variant: Variants | null;
  }) {
    const { athleteId, metadata, variant } = args;

    return [athleteId, metadata.id, metadata.field, variant]
      .filter(Boolean) // Removes null or undefined values
      .join("-");
  }

  static getAverageAndBestValue(data: number[], sortType: SortType): { best: number | null; average: number | null } {
    // Filter out null/undefined values to exclude athletes without scores
    const validValues = data.filter((v) => typeof v === "number" && !isNaN(v));

    if (!validValues.length) {
      return { best: null, average: null };
    }

    return { best: WidgetTargetHelpers.getBestValue(validValues, sortType), average: mean(validValues) };
  }

  static buildSessionData(data: Pick<WidgetCommonData, "latestSessionDate" | "variant">[]): SessionData[] {
    const sessionData: SessionData[] = [];

    data.forEach((entry) => {
      if (entry.latestSessionDate) {
        sessionData.push({
          date: entry.latestSessionDate,
          variant: entry.variant,
        });
      }
    });

    return sessionData;
  }

  static getTargetId(dashboard: Dashboard) {
    if (dashboard.type === "Athlete") {
      return dashboard.athleteId;
    }

    return dashboard.groupId;
  }
}

export default WidgetHelpers;
